/*!
 * Copyright (c) 2023 Bluemate B.V.
 */

@use '../../../../style/common-variables' as commons;
@use '../../../../style/breakpoint-tiers' as breakpoints;
@use '../../../../style/display-breakpoints' as display;
@use '../../../../style/color-functions' as *;

.bluemate-header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  display: grid;
  grid-row-gap: 1rem;
  z-index: commons.$z-index-popup-3;
  background-color: commons.$color-background-grey;
  // With viewport-fit=cover the webview extends under the status bar/notch on iOS
  // (black-translucent). Reserve the top inset so the header content sits below it;
  // 0 on every non-notched context, so desktop/browser are unaffected. The measured
  // header height (see header.tsx) includes this, keeping the spacer in sync.
  padding-top: env(safe-area-inset-top);

  @include breakpoints.bp-below-desktop-full {
    border-bottom: 1px solid commons.$color-gray-7;
  }

  // Mobile responsive overrides (mobile-platform). The base desktop .header-inner /
  // .header-content rules are defined further below (test-woj); these only add the
  // bp-below-desktop-full behaviour and the portal-select-label sizing.
  .header-inner {
    @include breakpoints.bp-below-desktop-full {
      justify-items: center;
      box-sizing: border-box;
    }

    .header-mobile-slot {
      display: flex;
      align-items: center;
      justify-content: center;
      // Sets the header's row height on mobile: 2.75rem grew to 41px once the
      // root font settled at 15px, making the whole bar taller than it needs to
      // be. 2.4rem is still a comfortable target for the menu button.
      width: 2.4rem;
      height: 2.4rem;

      .mobile-menu-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        cursor: pointer;
        font-size: 1.6rem;
        color: commons.$color-blue-text;
        transition: color 0.2s ease-in-out;
        -webkit-tap-highlight-color: transparent;

        &.active {
          color: commons.$color-blue-bluemate;
        }
      }
    }

    .logo-container {
      .logo {
        max-width: 10rem;
        cursor: pointer;
      }

      @include breakpoints.bp-below-desktop-full {
        width: 100%;
        display: flex;
        justify-content: center;

        // The uniform 15px root made every rem-based size grow on phones; the
        // logo is the one element that reads better at its previous width.
        .logo {
          max-width: 8.25rem;
        }
      }
    }

    .header-content {
      // CSS fallback: not rendered at all on the mobile layout (header.tsx shows only the logo);
      // guards against drift between the JS (useBreakpoint) and CSS boundaries.
      @include breakpoints.bp-below-desktop-full {
        display: none;
      }
    }
  }

  .header-content.left {
    margin-right: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    min-width: 0;

    .logo-container {
      @include display.break-lg-up {
        margin-right: 0.5rem;
      }
    }

    .menu-toggle {
      cursor: pointer;
      font-size: 1.25rem;
      flex-shrink: 0;
      transition: color 0.2s ease;

      &:hover {
        color: commons.$color-blue-bluemate;
      }
    }

    .logo {
      max-width: 10rem;
      cursor: pointer;

      @include breakpoints.bp-below-desktop-full {
        max-width: 8.25rem;
      }
    }
  }

  .header-inner {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    padding: 1rem 2rem 1rem 1rem;

    .header-content {
      display: flex;
      align-items: center;
      min-width: 0;

      .env {
        font-size: 1.2rem;
        color: red;
        font-weight: 600;
        margin-right: 1rem;
      }

      .header-actions {
        display: flex;
        align-items: flex-end;
        gap: 0.75rem;
      }

      .header-language-select {
        min-width: 8.5rem;
      }

      .page-title {
        font-weight: 500;
        font-size: 1.5rem;
        color: commons.$color-blue-text;
      }

      .invoices-left {
        font-weight: 500;
        color: commons.$color-blue-text;

        span {
          padding: 0.25rem;
          background-color: rgba(red, 0.1);
          color: red;
          border-radius: 0.25rem;
        }
      }

      .header-actions .user-info {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: commons.$color-blue-text;
        white-space: nowrap;
        min-height: calc(2.5rem + 2px);

        .user-icon {
          font-size: 2rem;
          color: commons.$color-blue-bluemate;
        }

        .sign-out-btn {
          color: commons.$color-gray-4;
          transition: color 0.2s ease-in-out;
          font-size: 0.9rem;
          cursor: pointer;

          &:hover {
            color: commons.$color-blue-1;
          }
        }
      }

      .header-actions .feedback-btn-anchor {
        display: inline-flex;
      }

      &.right {
        justify-content: flex-end;
        // Override the generic .header-content min-width: 0 so this track keeps its
        // content-based minimum, letting the right cluster size to its content at the
        // end of the row instead of overflowing its own grid cell.
        min-width: auto;

        .account-trigger {
          display: flex;
          align-items: center;
          cursor: pointer;
          color: commons.$color-blue-bluemate;
          transition: color 0.2s ease;

          .user-icon {
            font-size: 2rem;
          }

          &:hover {
            color: darken(commons.$color-blue-bluemate, 10%);
          }
        }
      }
    }

    // Last of the three same-range mobile blocks in this file, so it is the one
    // that decides the grid (a media query adds no specificity). Menu button on
    // the left, mirrored by an empty slot on the right, plus even left/right
    // padding — together they keep the logo centred on the screen.
    @include breakpoints.bp-below-desktop-full {
      grid-template-columns: 2.4rem minmax(0, 1fr) 2.4rem;
      padding: 0.5rem 1rem;
    }
  }
}

.header-account-dialog {
  // Three classes on purpose: BluemateDialog clips itself via
  // `.bluemate-dialog.default-popup { overflow: hidden }`, and this dialog needs
  // its account dropdown to escape the box. Equal specificity would leave the
  // winner to source order.
  &.bluemate-dialog.default-popup {
    overflow: visible;
  }

  .body-container {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .env {
    font-size: 1.2rem;
    color: red;
    font-weight: 600;
  }

  .header-actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 2rem;
    color: commons.$color-blue-text;

    .account-field {
      width: 100%;

      .context-account {
        width: 100%;
        grid-template-columns: 1fr;

        .grouped-account-select {
          width: 100%;
        }
      }

      &.user-info {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 0.75rem;

        .user-icon {
          font-size: 2rem;
          color: commons.$color-blue-bluemate;
        }

        .sign-out-btn {
          color: commons.$color-gray-4;
          transition: color 0.2s ease-in-out;
          font-size: 1rem;
          cursor: pointer;

          &:hover {
            color: commons.$color-blue-1;
          }
        }
      }
    }
  }

  .header-language-select {
    width: 100%;

    .bluemate-select-label {
      font-size: 0.8rem;
    }
  }
}

// The header's own selects (language, sales account) drop the PortalSelect
// trigger shadow — floating boxes read as raised cards against the flat header
// bar. The shadow stays on selects inside page content.
.bluemate-header {
  .portal-select .portal-select-container,
  .header-actions .bluemate-button {
    box-shadow: none;
  }
}

.header-language-option {
  display: inline-flex;
  align-items: center;
  column-gap: 0.5rem;
}
